import { FC } from "react";
import { Typography } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";

import { CardRadio } from "../card-radio/CardRadio";

/**
 * data structure for room templates
 */
export interface Template {
    /**
     * template entry id
     */
    id: string;
    /**
     * template name (translated)
     */
    name: string;
    /**
     * template description (translated)
     */
    description?: string;
    /**
     * name of illustration component to use for this template.
     * a matching component should exist in the illustrations map.
     * if not defined, a custom illustration will be used
     */
    illustration?: string;
}

interface TemplateSelectionProps {
    labelSingle: string;
    labelMultiple: string;
    templates: Template[];
    illustrationsMap: Record<string, FC>;
    defaultIllustration: FC;
}

const StyledTemplate = styled("div")(({ theme }) => ({
    margin: theme.spacing(2),
    display: "flex",
}));

const StyledTemplateText = styled("div")(({ theme }) => ({
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
}));

const StyledDescription = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        textAlign: "center",
    },
}));

/**
 * Templates component - provides an accessible grid of templates for townhall creation
 */
export const TemplateSelection = (props: TemplateSelectionProps) => {
    const { labelSingle, labelMultiple, templates, illustrationsMap, defaultIllustration: DefaultIllustration } = props;

    /**
     * returns the appropriate illustration component based on template data
     */
    const getIllustration = (template: Template) => {
        // if the template defines an illustration, use it.
        if (template.illustration && illustrationsMap[template.illustration]) {
            const Illustration = illustrationsMap[template.illustration];
            return <Illustration />;
        }
        // fallback to a custom illustration if no specific one is defined
        return <DefaultIllustration />;
    };

    return (
        <CardRadio
            id="roomMode"
            label={templates.length > 1 ? labelMultiple : labelSingle}
            cards={templates.map((template: Template, index: number) => {
                return {
                    id: template.id,
                    name: template.name,
                    content: (
                        <StyledTemplate>
                            {getIllustration(template)}
                            <StyledTemplateText>
                                <Typography variant={"body2Highlight"}>{template.name}</Typography>
                                <StyledDescription variant={"body2"}>{template.description}</StyledDescription>
                            </StyledTemplateText>
                        </StyledTemplate>
                    ),
                };
            })}
        />
    );
};
